/**
 * routes.js — the SEO manifest: one entry per indexable URL
 * ──────────────────────────────────────────────────────────
 * Shared by:
 *   • <Seo>               (runtime head updates, locale-reactive)
 *   • scripts/postbuild-seo.mjs  (static per-route <head> + sitemap + robots)
 *   • netlify/functions/card-page.js (shares SITE + escapeHtml + renderHead)
 *
 * Pure ESM, no JSX, no browser APIs.
 */

import { ORIGIN, graph, webPage, softwareApp, breadcrumbs, faqPage } from './schema.js';
import { HOME_FAQS, PRICING_FAQS, SEO_PAGES, SEO_PAGE_KEYS } from './content.js';

export const SITE = {
  origin: ORIGIN,
  name: 'CardPass',
  ogDefault: '/og/og-default.png',
  ogBusiness: '/og/og-business.png',
  themeColor: '#000000'
};

const T = (ar, en) => ({ ar, en });

/**
 * Route manifest. Keys are stable ids used by <Seo route="..."/>.
 *   path        — URL path ('' segments not allowed; '/' for home)
 *   title/desc  — {ar,en}
 *   ogImage     — site-relative path
 *   noindex     — robots noindex,nofollow (still prerendered so the URL
 *                 serves real HTML instead of falling through to the
 *                 card-page function)
 *   sitemap     — include in sitemap.xml (+ priority/changefreq)
 *   crumbs      — breadcrumb trail (home is prepended automatically)
 *   jsonld(locale) — page-specific schema nodes (site-wide Organization +
 *                 WebSite are always added by graph())
 */
export const ROUTES = {
  home: {
    path: '/',
    title: T('بطاقة عمل رقمية وكرت شخصي إلكتروني في Apple Wallet | كارد باس CardPass', 'CardPass | A Digital Business Card for Apple Wallet & Google Wallet'),
    desc: T(
      'أنشئ بطاقة عمل رقمية (كرت شخصي إلكتروني) في 30 ثانية، تعيش في Apple Wallet و Google Wallet وتتحدّث تلقائياً. شارك بياناتك بمسح QR، وخلّ عملاءك يحجزون اجتماعهم معك من بطاقتك. مجاناً، مصمّمة للسوق السعودي والخليجي.',
      'Create a digital business card in 30 seconds. It lives in Apple Wallet and Google Wallet, updates automatically, shares with one QR scan, and lets clients book a meeting with you from your card. Free, built for Saudi Arabia and the Gulf.'
    ),
    sitemap: { priority: 1.0, changefreq: 'weekly' },
    jsonld: (locale) => [
      softwareApp(locale),
      webPage({ path: '/', title: ROUTES.home.title[locale], description: ROUTES.home.desc[locale], locale }),
      faqPage(HOME_FAQS, locale)
    ]
  },

  pricing: {
    path: '/pricing',
    title: T('الأسعار — ابدأ بطاقتك الرقمية مجاناً | CardPass', 'Pricing — Start Your Digital Card Free | CardPass'),
    desc: T(
      'خطة مجانية للأبد تشمل باس Apple Wallet وQR يحفظ جهة الاتصال فوراً، مع تجربة 14 يوم للمزايا الإضافية. باقتا برو وبرو+ قريباً. بدون بطاقة ائتمانية.',
      'A free-forever plan with an Apple Wallet pass and an instant-save QR, plus a 14-day trial of the extras. Pro and Pro+ coming soon. No credit card required.'
    ),
    sitemap: { priority: 0.9, changefreq: 'weekly' },
    crumbs: [T('الأسعار', 'Pricing')],
    jsonld: (locale) => [
      softwareApp(locale),
      webPage({ path: '/pricing', title: ROUTES.pricing.title[locale], description: ROUTES.pricing.desc[locale], locale }),
      faqPage(PRICING_FAQS, locale)
    ]
  },

  business: {
    path: '/business',
    title: T('بطاقات عمل إلكترونية للموظفين والشركات في السعودية | CardPass للشركات', 'Digital Business Cards for Teams & Companies | CardPass for Business'),
    desc: T(
      'كروت عمل إلكترونية لكل موظفينك بهوية شركتك في Apple Wallet و Google Wallet، تضيفهم من ملف Excel وتديرهم من لوحة وحدة، مع فئات بطاقات حسب المستوى وحجز اجتماعات مع فريقك من بطاقاتهم، وتوفير في تكاليف الطباعة.',
      'Unify your brand on an Apple Wallet card for every employee from one central dashboard, with card tiers by level, meeting booking with your team from their cards, and real savings on printing.'
    ),
    ogImage: SITE.ogBusiness,
    sitemap: { priority: 0.9, changefreq: 'weekly' },
    crumbs: [T('للشركات', 'For Business')],
    jsonld: (locale) => [
      softwareApp(locale),
      webPage({ path: '/business', title: ROUTES.business.title[locale], description: ROUTES.business.desc[locale], locale })
    ]
  },

  signup: {
    path: '/signup',
    title: T('أنشئ بطاقتك الرقمية مجاناً — التسجيل | CardPass', 'Create Your Free Digital Card — Sign Up | CardPass'),
    desc: T(
      'سجّل بالإيميل أو Google وأصدر بطاقة عمل رقمية في Apple Wallet خلال دقيقة. بدون بطاقة ائتمانية.',
      'Sign up with email or Google and issue your Apple Wallet business card within a minute. No credit card required.'
    ),
    sitemap: { priority: 0.8, changefreq: 'monthly' },
    jsonld: (locale) => [webPage({ path: '/signup', title: ROUTES.signup.title[locale], description: ROUTES.signup.desc[locale], locale })]
  },

  login: {
    path: '/login',
    title: T('تسجيل الدخول | CardPass', 'Log In | CardPass'),
    desc: T('سجّل دخولك إلى لوحة تحكم CardPass لإدارة بطاقتك الرقمية.', 'Log in to your CardPass dashboard to manage your digital card.'),
    noindex: true
  },

  resetPassword: {
    path: '/reset-password',
    title: T('استعادة كلمة المرور | CardPass', 'Reset Password | CardPass'),
    desc: T('استعد الوصول إلى حسابك في CardPass.', 'Regain access to your CardPass account.'),
    noindex: true
  },

  privacy: {
    path: '/privacy',
    title: T('سياسة الخصوصية | CardPass', 'Privacy Policy | CardPass'),
    desc: T('كيف يجمع CardPass بياناتك ويحميها ويستخدمها — بشفافية كاملة.', 'How CardPass collects, protects, and uses your data — in full transparency.'),
    sitemap: { priority: 0.3, changefreq: 'yearly' },
    crumbs: [T('سياسة الخصوصية', 'Privacy')],
    jsonld: (locale) => [webPage({ path: '/privacy', title: ROUTES.privacy.title[locale], description: ROUTES.privacy.desc[locale], locale })]
  },

  terms: {
    path: '/terms',
    title: T('شروط الاستخدام | CardPass', 'Terms of Service | CardPass'),
    desc: T('الشروط والأحكام المنظمة لاستخدام منصة CardPass.', 'The terms and conditions governing the use of CardPass.'),
    sitemap: { priority: 0.3, changefreq: 'yearly' },
    crumbs: [T('شروط الاستخدام', 'Terms')],
    jsonld: (locale) => [webPage({ path: '/terms', title: ROUTES.terms.title[locale], description: ROUTES.terms.desc[locale], locale })]
  },

  calendarIntegration: {
    path: '/calendar-integration',
    title: T('ربط تقويم Google وMicrosoft | CardPass', 'Google and Microsoft Calendar Connection | CardPass'),
    desc: T('كيف يستخدم CardPass تقويمك لحجز المواعيد من بطاقتك، وما البيانات التي يصل إليها.', 'How CardPass uses your calendar to book meetings from your card, and what data it accesses.'),
    sitemap: { priority: 0.3, changefreq: 'yearly' },
    crumbs: [T('ربط التقويم', 'Calendar connection')],
    jsonld: (locale) => [webPage({ path: '/calendar-integration', title: ROUTES.calendarIntegration.title[locale], description: ROUTES.calendarIntegration.desc[locale], locale })]
  },

  // App surfaces — prerendered with noindex so crawlers get a definitive
  // answer (and they never fall through to the card-page function).
  app: { path: '/app', title: T('لوحة التحكم | CardPass', 'Dashboard | CardPass'), desc: T('إدارة بطاقتك.', 'Manage your card.'), noindex: true },
  admin: { path: '/admin', title: T('الإدارة | CardPass', 'Admin | CardPass'), desc: T('لوحة الإدارة.', 'Admin panel.'), noindex: true },
  scan: { path: '/scan', title: T('مسح بطاقة | CardPass', 'Scan a Card | CardPass'), desc: T('مسح بطاقات العمل الورقية.', 'Scan paper business cards.'), noindex: true },
  businessDashboard: { path: '/business-dashboard', title: T('لوحة الشركات | CardPass', 'Business Dashboard | CardPass'), desc: T('إدارة بطاقات فريقك.', 'Manage your team cards.'), noindex: true }
};

// ── SEO content pages (from content.js) ─────────────────────────
const SEO_PAGE_TITLES = {
  'digital-business-card': T('بطاقة عمل رقمية في السعودية — الدليل الشامل | CardPass', 'Digital Business Card in Saudi Arabia — The Complete Guide | CardPass'),
  'nfc-business-card': T('بطاقة NFC أم Apple Wallet؟ قارن قبل ما تشتري | CardPass', 'NFC Card or Apple Wallet? Compare Before You Buy | CardPass'),
  'apple-wallet-business-card': T('بطاقة أعمال في Apple Wallet — أنشئها في 30 ثانية | CardPass', 'Apple Wallet Business Card — Create Yours in 30 Seconds | CardPass'),
  'qr-code-business-card': T('بطاقة عمل بكود QR — حفظ جهة الاتصال بمسحة واحدة | CardPass', 'QR Code Business Card — Save a Contact in One Scan | CardPass'),
  'meeting-booking': T('حجز الاجتماعات من بطاقة عملك الرقمية | CardPass', 'Meeting Booking from Your Digital Business Card | CardPass'),
  'electronic-business-card': T('كرت شخصي إلكتروني مجاني — بطاقة أعمال إلكترونية في جوالك | CardPass', 'Free Electronic Business Card on Your Phone | CardPass'),
  'add-card-to-apple-wallet': T('كيف أضيف بطاقة عمل في محفظة الآيفون Apple Wallet؟ خطوة بخطوة | CardPass', 'How to Add a Business Card to Apple Wallet, Step by Step | CardPass'),
  'free-business-card-maker': T('تصميم بطاقة عمل مجانًا أونلاين — صمّم كرتك بنفسك | CardPass', 'Free Online Business Card Maker | CardPass'),
  'employee-business-cards': T('بطاقات عمل رقمية للموظفين والشركات في السعودية | CardPass للشركات', 'Digital Business Cards for Employees and Companies | CardPass for Business'),
  'business-card-printing-alternative': T('بديل طباعة الكروت الشخصية للموظفين — وفّر على شركتك | CardPass', 'Alternative to Printing Staff Business Cards | CardPass'),
  'sales-team-business-cards': T('بطاقات رقمية لفريق المبيعات مع حجز الاجتماعات | CardPass للشركات', 'Digital Cards for Sales Teams with Meeting Booking | CardPass'),
  'cardpass-vs-blinq': T('كارد باس أم Blinq؟ مقارنة بطاقات الموظفين الرقمية | CardPass', 'CardPass vs Blinq: Employee Digital Business Cards Compared | CardPass'),
  'cardpass-vs-hihello': T('كارد باس أم HiHello؟ مقارنة بطاقات الشركات الرقمية | CardPass', 'CardPass vs HiHello: Company Digital Business Cards Compared | CardPass')
};
const SEO_PAGE_DESCS = {
  'digital-business-card': T(
    'كل ما تحتاج معرفته عن بطاقة العمل الرقمية: كيف تعمل، مميزاتها مقابل الكروت الورقية وNFC، وكيف تنشئ بطاقتك في Apple Wallet خلال 30 ثانية — مجاناً.',
    'Everything about digital business cards: how they work, how they beat paper and NFC, and how to create yours in Apple Wallet in 30 seconds — free.'
  ),
  'nfc-business-card': T(
    'تفكر تشتري بطاقة NFC بـ 150–400 ريال؟ قارن أولاً: التكلفة، التحديث، التوافق، وتجربة المشاركة — ولماذا بطاقة Apple Wallet بالـ QR تغنيك عنها مجاناً.',
    'Before paying 150–400 SAR for an NFC card, compare: cost, updates, compatibility, and the sharing experience — and why a free Apple Wallet QR card replaces it.'
  ),
  'apple-wallet-business-card': T(
    'حوّل بيانات تواصلك إلى باس حقيقي في Apple Wallet: يفتح من شاشة القفل، يتحدّث تلقائياً عبر Push، ويُحفظ عند الطرف الآخر بمسح QR واحد. ابدأ مجاناً.',
    'Turn your contact details into a real Apple Wallet pass: opens from the lock screen, auto-updates via push, and saves on the other side with one QR scan. Start free.'
  ),
  'qr-code-business-card': T(
    'بطاقة QR ذكية: الطرف الآخر يمسح الكود فتنحفظ بياناتك في جهات اتصاله مباشرة — بدون تطبيق وبدون كتابة، وحتى بدون إنترنت. أنشئ بطاقتك المجانية الآن.',
    'A smart QR card: one scan saves your details straight into their contacts — no app, no typing, even offline. Create your free card now.'
  ),
  'meeting-booking': T(
    'خلّ عملاءك يحجزون اجتماعهم معك من بطاقتك: يشوفون أوقاتك الفاضية من تقويم Google أو Outlook، والاجتماع ينزل في تقويمك مع رابط Meet أو Teams. مجاني حاليًا للأفراد.',
    'Let clients book a meeting with you from your card: they see your free times from Google or Outlook Calendar, and the meeting lands in your calendar with a Meet or Teams link. Free for individuals for now.'
  ),
  'electronic-business-card': T(
    'سوّ كرتك الشخصي الإلكتروني (بزنس كارد) مجانًا: بطاقة أعمال إلكترونية في Apple Wallet و Google Wallet، يحفظها العميل بمسح QR، وتتحدّث لما تغيّر بياناتك. بديل الكروت الورقية للأفراد والشركات.',
    'Make your electronic business card for free: it lives in Apple Wallet and Google Wallet, clients save it with one QR scan, and it updates when your details change. The paper card replacement for people and companies.'
  ),
  'add-card-to-apple-wallet': T(
    'طريقة إضافة بطاقة عمل أو كرت شخصي لمحفظة الآيفون Apple Wallet، وفي Google Wallet للأندرويد، خطوة بخطوة. ليش ما تنضاف الصورة، وكيف تطلع البطاقة من شاشة القفل. مجانًا.',
    'How to add a business card to Apple Wallet on iPhone, and to Google Wallet on Android, step by step. Why a photo can’t be added, and how to open the card from the lock screen. Free.'
  ),
  'free-business-card-maker': T(
    'صمّم بطاقة عملك مجانًا من المتصفح: ألوانك وشعارك وصورتك مع معاينة حيّة، وتكون جاهزة في محفظة جوالك تشاركها بكود QR. بدون مصمم وبدون طباعة.',
    'Design your business card for free in the browser: your colors, logo and photo with a live preview, ready in your phone wallet and shared by QR. No designer, no printing.'
  ),
  'employee-business-cards': T(
    'بطاقات عمل رقمية لكل موظفين شركتك بهوية وحدة في Apple Wallet و Google Wallet: استيراد من Excel، فئات بألوان، تعديل يتحدّث لحاله، وحجز اجتماعات مع الفريق. التفعيل خلال أسبوع.',
    'Digital business cards for every employee in one brand, in Apple Wallet and Google Wallet: Excel import, colored tiers, edits that update by themselves, and meeting booking with your team. Live within a week.'
  ),
  'business-card-printing-alternative': T(
    'كل موظف جديد وكل ترقية تعني كروت جديدة تنطبع والقديمة ترمى. انقل شركتك لبطاقات عمل رقمية تتعدّل من لوحة وحدة ولا تنطبع أبدًا، ويحفظها العميل بمسحة QR.',
    'Every new hire and promotion means reprinting and binning cards. Move your company to digital business cards edited from one dashboard, never printed, and saved by clients with one QR scan.'
  ),
  'sales-team-business-cards': T(
    'بطاقات رقمية لفريق المبيعات: العميل يحفظ المندوب بمسح QR ويحجز الاجتماع الجاي من نفس البطاقة في تقويم Microsoft 365 برابط Teams. بهوية الشركة ومن لوحة وحدة.',
    'Digital cards for sales teams: clients save the rep with one QR scan and book the next meeting from the same card into Microsoft 365 with a Teams link. On-brand, from one dashboard.'
  ),
  'cardpass-vs-blinq': T(
    'مقارنة صريحة بين كارد باس و Blinq لبطاقات الموظفين: العربية، حجز الاجتماعات، ربط الأنظمة، إضافة الموظفين، والأسعار. وين يتفوّق كل واحد.',
    'An honest CardPass vs Blinq comparison for employee cards: Arabic, meeting booking, integrations, adding employees, and pricing. Where each one wins.'
  ),
  'cardpass-vs-hihello': T(
    'مقارنة صريحة بين كارد باس و HiHello لبطاقات الشركات: العربية، حجز الاجتماعات من البطاقة، مزامنة الموظفين، والأسعار. وين يتفوّق كل واحد للشركات في السعودية.',
    'An honest CardPass vs HiHello comparison for company cards: Arabic, booking from the card, employee sync, and pricing. Where each one wins for companies in Saudi Arabia.'
  )
};

for (const key of SEO_PAGE_KEYS) {
  const page = SEO_PAGES[key];
  ROUTES[key] = {
    path: `/${key}`,
    title: SEO_PAGE_TITLES[key],
    desc: SEO_PAGE_DESCS[key],
    sitemap: { priority: 0.8, changefreq: 'monthly' },
    crumbs: [page.nav],
    jsonld: (locale) => [
      softwareApp(locale),
      webPage({ path: `/${key}`, title: SEO_PAGE_TITLES[key][locale], description: SEO_PAGE_DESCS[key][locale], locale }),
      faqPage(page.faqs, locale)
    ]
  };
}

// ─────────────────────────────────────────────────────────────────
export const escapeHtml = (s = '') =>
  String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Resolve the full head model for a route key (or a custom override object). */
export function getHead(routeKey, locale = 'ar', overrides = {}) {
  const r = typeof routeKey === 'string' ? ROUTES[routeKey] : routeKey;
  if (!r) return null;
  const path = overrides.path ?? r.path;
  const title = overrides.title ?? r.title[locale];
  const description = overrides.description ?? r.desc[locale];
  const ogImage = overrides.ogImage ?? r.ogImage ?? SITE.ogDefault;
  const crumbTrail = r.crumbs
    ? breadcrumbs([
        { name: locale === 'ar' ? 'الرئيسية' : 'Home', path: '/' },
        ...r.crumbs.map((c) => ({ name: c[locale], path }))
      ])
    : null;
  const nodes = [...(r.jsonld ? r.jsonld(locale) : []), crumbTrail, ...(overrides.extraJsonld || [])];
  return {
    title,
    description,
    canonical: `${SITE.origin}${path === '/' ? '/' : path}`,
    ogImage: ogImage.startsWith('http') ? ogImage : `${SITE.origin}${ogImage}`,
    ogType: overrides.ogType ?? 'website',
    noindex: overrides.noindex ?? !!r.noindex,
    locale,
    jsonld: graph(...nodes)
  };
}

/**
 * Render a head model to static HTML (build script + card-page function).
 * Keep the selector shapes in sync with <Seo>'s runtime upserts.
 */
export function renderHead(head) {
  const e = escapeHtml;
  const lines = [
    `<title>${e(head.title)}</title>`,
    `<meta name="description" content="${e(head.description)}" />`,
    head.noindex ? `<meta name="robots" content="noindex, nofollow" />` : null,
    `<link rel="canonical" href="${e(head.canonical)}" />`,
    `<meta property="og:site_name" content="${SITE.name}" />`,
    `<meta property="og:type" content="${e(head.ogType)}" />`,
    `<meta property="og:url" content="${e(head.canonical)}" />`,
    `<meta property="og:title" content="${e(head.title)}" />`,
    `<meta property="og:description" content="${e(head.description)}" />`,
    `<meta property="og:image" content="${e(head.ogImage)}" />`,
    `<meta property="og:image:width" content="1200" />`,
    `<meta property="og:image:height" content="630" />`,
    `<meta property="og:locale" content="${head.locale === 'ar' ? 'ar_SA' : 'en_US'}" />`,
    `<meta property="og:locale:alternate" content="${head.locale === 'ar' ? 'en_US' : 'ar_SA'}" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${e(head.title)}" />`,
    `<meta name="twitter:description" content="${e(head.description)}" />`,
    `<meta name="twitter:image" content="${e(head.ogImage)}" />`,
    `<script type="application/ld+json" id="seo-jsonld">${JSON.stringify(head.jsonld).replace(/</g, '\\u003c')}</script>`
  ].filter(Boolean);
  return lines.join('\n    ');
}
